import * as React from "react";
import { observer } from "mobx-react-lite";
import { getErrorMessage, onError } from "src/common/onError";
import { Alert, Button, Spin, Tag } from "antd";
import { LockOutlined, PlusOutlined } from "@ant-design/icons";
import { AColumn } from "src/components/tables/AsyncTable";
import { EntityCardDataExt } from "src/pages/EntityCardPage/EntityCardStore";
import { ifDef } from "src/common/ifDef";
import { selectOnRowClick } from "src/components/tables/selectOnRowClick";
import { ScrollableTable } from "src/components/ScrollableTable";
import styles from "./Mc2Summary.module.less";
import { ZMc2 } from "../Mc2Types";
import { Mc2EditorModal } from "./Mc2EditorModal";
import { mc2SummaryStore as store } from "./mc2SummaryStore";
import { Mc2SummaryActions } from "./actions";
import { Mc2Entity } from "./Mc2Entity";
import { Mc2StatesChangeModal } from "./actions/Mc2StatesChangeModal";
import { hasMc2Permission, Mc2Permissions } from "../mc2Permissions";

interface PropsMc2Summary {
  data: EntityCardDataExt;
}

export const Mc2Summary: React.FC<PropsMc2Summary> = observer((props) => {
  const { data } = props;
  const { entity, object } = data;
  React.useEffect(() => {
    if (entity) {
      store
        .init({
          modelEntityId: entity.id,
          modelObjectId: object.id,
        })
        .catch(onError);
    }
  }, [data]);
  if (!entity)
    return <Alert type="error" message="Отсутствует экземпляр модели" />;
  const { entityStore } = store;
  if (entityStore) {
    return <Mc2Entity store={entityStore} />;
  }
  return (
    <div className={styles.box}>
      {store.buzy && (
        <div className={styles.loader}>
          <Spin size="large" />
        </div>
      )}
      <div className={styles.pads}>
        <div className={styles.tools}>
          <div className={styles.toolSpace} />
          <Mc2SummaryActions />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => store.setMode("create")}
            disabled={!!store.createReason || store.buzy}
            title={store.createReason}
          >
            Создать Measurement Chart
          </Button>
        </div>
        {store.lastError ? (
          <Alert type="error" {...getErrorMessage(store.lastError)} />
        ) : (
          <Mc2SummaryTable />
        )}
      </div>
      <Mc2EditorModal
        title="Новый экземпляр объекта MC"
        open={store.mode === "create"}
        close={() => store.setMode(null)}
        onSelect={(values) => store.createMc(values)}
        sizeLineAttrNameId={store.sizeLineAttrNameId}
      />
      <Mc2StatesChangeModal
        open={store.mode === "states"}
        close={() => store.setMode(null)}
        currentStateId={Array.from(store.statesSet)[0] ?? ""}
        statesDict={store.statesDict}
        mcObjectId={store.settings.objMcId!}
        process={(dstStateId) => store.changeState(dstStateId)}
      />
    </div>
  );
});

const Mc2SummaryTable: React.FC = observer(() => {
  const { tableStore } = store;
  if (!tableStore) return null;
  return (
    <ScrollableTable
      store={tableStore}
      columns={columns}
      onRowClick={(row) => selectOnRowClick(row, tableStore)}
      minHeight="100px"
    />
  );
});

const columns: AColumn<ZMc2>[] = [
  {
    key: "state",
    title: "Состояние",
    render: (_, row) =>
      ifDef(row.stateId, (id) => (
        <Tag color="gold">{store.statesDict[id]?.name ?? id}</Tag>
      )),
    width: "15%",
    sorter: true,
  },
  {
    key: "name",
    title: "Название",
    render: (_, row) => (
      <Button
        type="link"
        onClick={(e) => {
          e.stopPropagation();
          store.openEntity(row);
        }}
        icon={
          hasMc2Permission(row, Mc2Permissions.create) ? undefined : (
            <LockOutlined />
          )
        }
      >
        {row.name}
      </Button>
    ),
    sorter: true,
  },
  {
    key: "sizeLineName",
    title: "Размерный ряд",
    render: (_, row) => row.sizeLineName,
    width: "20%",
    sorter: true,
  },
  {
    key: "baseSize",
    title: "Базовый размер",
    render: (_, row) => row.baseSize,
    width: "10%",
  },
  {
    key: "comment",
    title: "Комментарий",
    render: (_, row) => row.comment,
    width: "20%",
  },
];
